import { get } from 'lodash';
import { logger } from 'src/utils';

import {
  JobCostCodeSummaryGpDto,
  JobCostDetailDto,
  JobDto,
  JobSummaryDto,
  PostDto,
} from 'src/api/FiretrolApiClient';

import { JobData } from './components/JobDetails';

// Extended JobDto type for CTC view that includes posts on the linked job.
export type CTCJobDto = JobDto & { posts?: PostDto[] };

export const CTC_FILTER_KEY = '@firetrol_ctc_filters:06112025';

/**
 * Extracts saved division-filter names from localStorage. Tolerates missing
 * keys, invalid JSON, and malformed entries; in any error case the bogus
 * value is removed and an empty list returned.
 */
// @TODO: Replace this localStorage mumbo-jumbo with something safer.
export function readDivisionFilters(): string[] {
  try {
    const stored = localStorage.getItem(CTC_FILTER_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);

    if (Array.isArray(parsed?.divisions)) {
      return parsed.divisions
        .filter(
          (d: unknown): d is { name: string } =>
            typeof d === 'object' &&
            d !== null &&
            'name' in d &&
            typeof (d as { name: unknown }).name === 'string',
        )
        .map((d: { name: string }) => d.name);
    }
    if (parsed?.divisions !== undefined) {
      // divisions exists but is not an array - clear invalid data
      logger.warn('Invalid CTC filter format, clearing localStorage');
      localStorage.removeItem(CTC_FILTER_KEY);
    }
    return [];
  } catch (err) {
    logger.error('Error parsing CTC filters from localStorage', { err });
    localStorage.removeItem(CTC_FILTER_KEY);
    return [];
  }
}

/**
 * Extracts a Job Type label from a `NN-Name` division string. Returns 'N/A'
 * if the value is missing or has no segment after the separator.
 */
export function getJobType(divisions: string | undefined): string {
  if (!divisions) return 'N/A';
  const items = divisions.split('-');
  return items[1] ? items[1] : 'N/A';
}

/**
 * Aggregates the various per-job financial values that feed the JobDetails
 * cards. Pure function of the loaded API payload; no React state.
 */
export function computeJobData(
  job: JobSummaryDto,
  firstDetail: JobCostDetailDto | undefined,
  summaryList: JobCostCodeSummaryGpDto[],
): JobData {
  const original = firstDetail
    ? Number(get(firstDetail, 'originalContract', 0))
    : 0;
  const postedCOTotal = firstDetail ? Number(firstDetail.postedCoTotal) : 0;
  const contractToDate = original + postedCOTotal;
  const adjustedRevenue = firstDetail ? Number(firstDetail.revenueAdj) : 0;
  const expectedContract = contractToDate + adjustedRevenue;

  let costAtCompletion = 0;
  let costToDate = 0;
  summaryList.forEach((s) => {
    costAtCompletion += Number(s.ctc) + Number(s.jtdCost);
    costToDate += Number(s.jtdCost);
  });

  const grossMarginDollar = expectedContract - costAtCompletion;
  const grossMarginPercent =
    Math.round(grossMarginDollar / expectedContract) * 100;
  const grossBilled = firstDetail ? Number(firstDetail.grossBilled) : 0;
  const retentionHeld = firstDetail ? Number(firstDetail.retentionHeld) : 0;
  const retentionBilled = firstDetail ? Number(firstDetail.retentionBilled) : 0;
  const netBilled = grossBilled + retentionBilled - retentionHeld;
  const jtdCosts = Number(job.periodJtdCosts);

  const contractEarned =
    costAtCompletion !== 0
      ? (costToDate / costAtCompletion) * expectedContract
      : 0;
  const overUnder = grossBilled - contractEarned;
  const percentComplete =
    costAtCompletion !== 0
      ? Math.round((costToDate / costAtCompletion) * 100)
      : 0;

  return {
    contractAmount: job.contractAmt,
    original,
    overUnder,
    contractEarned,
    postedCOTotal,
    contractToDate,
    adjustedRevenue,
    expectedContract,
    costAtCompletion,
    grossMarginDollar,
    grossMarginPercent: Number.isFinite(grossMarginPercent)
      ? grossMarginPercent
      : 0,
    grossBilled,
    percentComplete,
    costToDate,
    retentionHeld,
    retentionBilled,
    netBilled,
    jtdCosts,
  };
}
